import React from "react";
import { node, string, bool } from "prop-types";
import Head from "next/head";
import dynamic from "next/dynamic";

import Footer from "@components/Footer/Footer";
import Header from "@components/Header/Header";
import PageTransition from "@components/PageTransition/PageTransition";
import ErrorBoundary from "@components/shared/ErrorBoundary";
import TestDriveLink from "@components/TestDrive/TestDriveLink";

import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { useMetaData } from "../../seo/useMetaData";
import {
  useCurrentDealStateSelector,
  useIsCEESubsidySelectedSelector,
} from "@redux/reducers/deal";
import { COUNTRIES, getIsCountry } from "@lib/getIsCountry";
import {
  useCEESubsidySelector,
  useIsUIv2Selector,
} from "@redux/reducers/appConfig";
import FinancialPanel from "@components/Footer/FinancialPanel";
import CEESubsidyPanel from "@components/Footer/CEESubsidyPanel";

import { useAvailablePreconfigurations } from "@hooks/useAvailablePreconfigurations";

const FinancialWidget = dynamic(
  () => import("../FinancialWidget/FinancialWidget"),
  { ssr: false }
);

const Layout = ({
  children,
  dealState,
  isGetPageNameByPathname,
  isPageNotFound,
  namePreconfiguration,
  noAnimation,
  saveProgressSource,
  withCEESubsidyPanel,
  withFinancialPanel,
  withSave,
  withTestDriveLink,
}) => {
  const availablePreconfigurations = useAvailablePreconfigurations();

  const isUIv2 = useIsUIv2Selector();

  const ceeSubsidySelected = useIsCEESubsidySelectedSelector();
  const ceeSubsidy = useCEESubsidySelector(ceeSubsidySelected);

  const currentDealState = useCurrentDealStateSelector();
  dealState = dealState ?? currentDealState;

  const { title, description } = useMetaData(
    namePreconfiguration,
    isGetPageNameByPathname,
    isPageNotFound
  );

  const showFinancialPanel =
    availablePreconfigurations.length > 0 &&
    withFinancialPanel &&
    isUIv2 &&
    getIsCountry(COUNTRIES.GB);

  const showCeeSubsidyPanel =
    availablePreconfigurations.length > 0 &&
    withCEESubsidyPanel &&
    ceeSubsidy.enabled &&
    ceeSubsidySelected;

  return (
    <ErrorBoundary>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="theme-color" content="#333" />
        <link rel="icon" href={getStaticAssetURL("/favicon.ico")} />
      </Head>
      <div className="flex flex-col min-h-dvh bg-primaryLight relative">
        <Header dealState={dealState} />
        <PageTransition noAnimation={noAnimation}>
          <main className="w-full flex-auto flex-shrink-0">{children}</main>
        </PageTransition>

        <FinancialWidget />

        {showFinancialPanel && <FinancialPanel />}

        {showCeeSubsidyPanel && (
          <CEESubsidyPanel ceeSubsidyType={ceeSubsidySelected} />
        )}

        {withTestDriveLink && <TestDriveLink />}

        <Footer withSave={withSave} saveProgressSource={saveProgressSource} />
      </div>
    </ErrorBoundary>
  );
};

Layout.propTypes = {
  children: node.isRequired,
  dealState: string,
  isGetPageNameByPathname: bool,
  isPageNotFound: bool,
  namePreconfiguration: string,
  noAnimation: bool,
  saveProgressSource: string,
  withCEESubsidyPanel: bool,
  withFinancialPanel: bool,
  withSave: bool,
  withTestDriveLink: bool,
};

export default Layout;
